
import { Component, Ref, Vue } from 'vue-property-decorator'
import StudentPersonalLearningMapProgress from '@/components/templates/manage/StudentPersonalLearningMapProgress.vue'
import { SelectItem } from '@/models/SelectItem'
import { SystemChartDetail, SystemChartUnit, SystemChart } from '@/models/SystemChart'
import { LearningProgress } from '@/const/LearningProgress'
import { SubjectCode } from '@/const/SubjectCode'
import { UsersApi } from '@/api/methods/UsersApi'
import { SystemChartsApi } from '@/api/methods/SystemChartsApi'
import { CurriculumUnitKnowledgeMapsApi } from '@/api/methods/CurriculumUnitKnowledgeMapsApi'

/**
 * 生徒別学習進捗(知識マップ)
 */
@Component({
  components: {
    StudentPersonalLearningMapProgress,
  },
})
export default class StudentLearningMapProgress extends Vue {
  /** 知識マップ学習進捗レイアウト */
  @Ref()
  studentPersonalLearningMapProgress!: StudentPersonalLearningMapProgress

  /** 初期処理完了フラグ */
  private isLoaded = false

  /** フィルターDisabled時の固定値 */
  private readonly OPTION_DISABLED = { value: '', label: '知識マップがありません' }

  /** 生徒ニックネーム */
  private studentName = ''

  /** 系統図のリスト */
  private systemChartList: SystemChart[] = []

  /** 選択中の系統図ID */
  private currentChartId: number = 0

  /** 知識MAP詳細情報 */
  private systemChartDetail: SystemChartDetail | null = null

  /** 知識マップ選択ボタンのDisabled制御 */
  private isDisabledMapFilter = false

  /** 遷移元の情報を復元 */
  private prevPage = this.$prevPage.restorePage()

  /**
   * パラメータから取得した生徒ID
   *
   * @returns 生徒ID
   */
  private get studentId(): number {
    return Number(this.$route.params.userId)
  }

  /**
   * 教科一覧取得(フィルター設定)
   *
   * @returns 教科一覧
   */
  private get filterSubjectList(): SelectItem[] {
    // 教科一覧を成形
    return SubjectCode.LIST.map((subject) => {
      return {
        value: subject.code,
        label: subject.label,
      }
    })
  }

  /**
   * 教科一覧デフォルト値
   *
   * @returns 教科コード
   */
  private get defaultSubjectCode(): string {
    let subjectCode = this.prevPage?.query.subjectCode

    // クエリに学年コードが存在しない場合、空文字を代入
    if (!subjectCode || typeof subjectCode !== 'string') {
      subjectCode = ''
    }

    // 教科の存在チェック
    const subject = SubjectCode.findByCode(subjectCode)

    return subject?.code || SubjectCode.LIST[0].code
  }

  /**
   * 選択中の教科コード
   *
   * @returns 教科コード
   */
  private get selectedSubjectCode(): string {
    return this.studentPersonalLearningMapProgress.selectedSubjectCode
  }

  /**
   * 知識マップ一覧(フィルター用)
   *
   * @returns 知識マップ一覧
   */
  private get filterMapList(): SelectItem[] {
    // プルダウンが非活性の場合は固定値を返す
    if (this.isDisabledMapFilter) {
      return [this.OPTION_DISABLED]
    }

    return this.systemChartList.map((chart) => {
      return {
        value: `${chart.id}`,
        label: chart.name,
      }
    })
  }

  /**
   * 知識マップIDのプルダウン値
   *
   * @returns ID(文字列型)
   */
  private get currentMapIdKey(): string {
    // プルダウンが非活性の場合は固定値を返す
    if (this.isDisabledMapFilter) {
      return this.OPTION_DISABLED.value
    }

    // クエリ情報から系統図IDを取得
    const systemChartId = this.prevPage?.query.systemChartId
    // 系統図IDの存在チェック
    const systemChart = this.filterMapList.find((map) => map.value === systemChartId)

    // クエリの系統図IDを返す || 選択中のIDを文字列化して渡す
    return systemChart?.value || `${this.currentChartId}`
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 生徒情報取得
    const student = await UsersApi.getStudentInformation(this.studentId)

    // ニックネーム設定
    this.studentName = student.nickname

    // 系統図一覧取得
    await this.getLearningMapList(this.defaultSubjectCode)

    // 進捗情報取得
    await this.loadChartUnits()

    this.isLoaded = true

    this.$loading.complete()
  }

  /**
   * 教科変更時のイベント
   */
  private async changeSubjectFilter(): Promise<void> {
    this.$loading.start()

    // 知識マップ一覧の再読み込み
    await this.getLearningMapList(this.selectedSubjectCode)

    // 知識マップの選択値を変更
    this.studentPersonalLearningMapProgress.mapFilter.setCurrentValue(this.currentMapIdKey)

    // 進捗情報取得
    await this.loadChartUnits()

    this.$loading.complete()
  }

  /**
   * 知識マップ変更時のイベント
   */
  private async changeMapFilter(): Promise<void> {
    this.$loading.start()

    this.currentChartId = this.studentPersonalLearningMapProgress.selectedMapId

    // 進捗情報取得
    await this.loadChartUnits()

    this.$loading.complete()
  }

  /**
   * 選択中の系統図情報を引き当てる
   *
   * @returns 系統図情報(未選択などの場合はundefined)
   */
  private get currentChart(): SystemChart | undefined {
    return this.systemChartList.find((chart) => {
      return chart.id === this.currentChartId
    })
  }

  /**
   * 知識マップ一覧取得
   *
   * @param subjectCode 教科コード
   */
  private async getLearningMapList(subjectCode: string): Promise<void> {
    const ststemCharts = await SystemChartsApi.getSystemCharts(subjectCode)
    this.systemChartList = ststemCharts.map((chart) => {
      return {
        id: chart.id,
        code: chart.code,
        name: chart.name,
        imageUrl: chart.imageUrl,
      }
    })

    // 選択中の系統図を変更する
    if (this.systemChartList.length == 0) {
      // 系統図一覧が0件の場合は未選択状態に戻し、Disabledにする
      this.currentChartId = 0
      this.isDisabledMapFilter = true
    } else {
      // 先頭要素を初期選択値として設定する
      this.currentChartId = this.systemChartList[0].id
      this.isDisabledMapFilter = false
    }
  }

  /**
   * 系統図の単元情報取得
   */
  private async loadChartUnits(): Promise<void> {
    // 保持している詳細情報をリセット
    this.systemChartDetail = null

    if (!this.currentChart) {
      // 系統図情報が未選択の場合は処理を終了(系統図が存在しない場合など)
      return
    }

    // 単元情報取得
    const curriculumUnitKnowledgeMaps = await CurriculumUnitKnowledgeMapsApi.getCurriculumUnitKnowledgeMaps(
      this.currentChartId,
      this.studentId
    )

    // 単元情報をフロント用に整形
    const units: SystemChartUnit[] = curriculumUnitKnowledgeMaps.flatMap((knowledgeCategory) => {
      return knowledgeCategory.units.map((unit) => {
        return {
          id: unit.id,
          name: unit.name,
          viewNumber: unit.viewNumber,
          colorCode: knowledgeCategory.colorCode,
          xPosition: unit.xPosition,
          yPosition: unit.yPosition,
          learningProgress: LearningProgress.findByCode(unit.progress),
        }
      })
    })

    // 知識マップ情報の設定
    this.systemChartDetail = {
      units: units,
      imageUrl: this.currentChart.imageUrl,
    }
  }

  /**
   * 単元選択ボタン押下時のイベント
   *
   * @param id 増進堂単元ID
   */
  public async onSelectUnit(id: number): Promise<void> {
    // TODO: 将来的に詳細表示処理を実装するが、現状は何もしない
    this.$logger.log(id)
  }

  /**
   * 戻るボタン押下時の処理
   */
  private eventClickBackButton(): void {
    // 保持されたクエリ情報を取得
    const prevPage = this.$prevPage.restorePage()

    if (!prevPage) {
      // クエリが存在しない場合、知識MAPの進捗一覧へ遷移
      this.$router.push('/teacher/students/progress/map')
      return
    }

    // クエリを元に遷移
    this.$router.push({
      path: prevPage.path,
      query: prevPage.query,
    })
  }

  /**
   * 進捗タブクリック時の処理
   */
  private eventClickStudentProgressTab(): void {
    this.$router.push({
      path: `/teacher/students/${this.studentId}/progress`,
      query: this.$route.query,
    })
  }
}
